import { useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, HeartHandshake, ArrowRight, RotateCcw } from "lucide-react";
import { toBullets } from "@/lib/textBullets";
import TellMeMoreButton from "@/components/TellMeMoreButton";
import MomentBanner from "@/components/MomentBanner";
import QuestionFlagTrigger from "@/components/QuestionFlagTrigger";

const WRONG_ACK_OPTIONS = [
  "Oops, not quite — but that's okay!",
  "Almost there — let's work it out together.",
  "Not this time — let's see where it went.",
  "That was a tricky one — let's sort it out!",
];

function BulletList({ items, testId, dotClass }) {
  return (
    <ul className="space-y-2" data-testid={testId}>
      {items.map((item, idx) => (
        <li key={idx} className="flex gap-3 items-start" data-testid={`${testId}-item-${idx}`}>
          <span className={`mt-2 w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotClass}`} aria-hidden="true" />
          <span className="text-slate-800 text-base md:text-lg leading-relaxed">{item}</span>
        </li>
      ))}
    </ul>
  );
}

export default function ExplanationCard({ payload, onNext, onReviewMistake, sessionId }) {
  const correct = payload?.is_correct;
  const moment = payload?.mitraa_moment;
  // Pick a random warm acknowledgement on mount for this question — stable across re-renders.
  const wrongAck = useMemo(
    () => WRONG_ACK_OPTIONS[Math.floor(Math.random() * WRONG_ACK_OPTIONS.length)],
    [payload?.record_id],
  );
  return (
    <div className="fade-up max-w-3xl mx-auto" data-testid="explanation-card">
      {/* Gamification Moment Banner */}
      <MomentBanner moment={moment} className="mb-4" />
      
      <div className={`rounded-3xl p-8 md:p-10 mit-shadow border ${correct ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
        {!correct && (
          <p className="text-sm text-amber-700/80 font-medium mb-2" data-testid="explanation-ack-line">
            {wrongAck}
          </p>
        )}
        <div className="flex items-center gap-3 mb-3">
          {correct ? (
            <CheckCircle2 className="w-7 h-7 text-emerald-600" data-testid="explanation-correct-icon" />
          ) : (
            <HeartHandshake className="w-7 h-7 text-amber-600" data-testid="explanation-wrong-icon" />
          )}
          <h3 className={`font-display text-2xl font-bold ${correct ? "text-emerald-800" : "text-amber-800"}`} data-testid="explanation-headline">
            {correct ? "That's right! Nicely done." : "Let's look at this together."}
          </h3>
        </div>

        {!correct && (
          <p className="text-slate-700 mb-5" data-testid="explanation-correct-answer">
            The answer is <strong className="text-emerald-700">{payload.correct_answer}</strong>.
          </p>
        )}

        {payload?.crisp && (
          <Card className="rounded-2xl p-5 bg-white border-slate-100 mb-3" data-testid="explanation-crisp">
            <BulletList items={toBullets(payload.crisp)} testId="explanation-crisp-list" dotClass="bg-sky-500" />
          </Card>
        )}

        {payload?.elaborate && (
          <Card className="rounded-2xl p-5 bg-slate-50 border-dashed border-slate-200 mb-3" data-testid="explanation-elaborate">
            <div className="text-xs uppercase font-bold text-slate-500 tracking-wider mb-3">To elaborate</div>
            <BulletList items={toBullets(payload.elaborate)} testId="explanation-elaborate-list" dotClass="bg-slate-400" />
          </Card>
        )}

        {!correct && payload?.record_id && (
          <TellMeMoreButton scope="explanation" recordId={payload.record_id} subject={payload.subject} />
        )}

        <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-end">
          {payload?.showReviewButton && (
            <Button
              variant="outline"
              onClick={onReviewMistake}
              className="rounded-full border-2 border-amber-300 text-amber-700 hover:bg-amber-100 font-semibold tap-44 px-6 py-5"
              data-testid="review-mistake-btn"
            >
              <RotateCcw className="w-4 h-4 mr-2" /> Review with me
            </Button>
          )}
          <Button
            onClick={onNext}
            className="rounded-full bg-sky-500 hover:bg-sky-600 text-white font-semibold px-8 py-6 tap-44"
            data-testid="explanation-next-btn"
          >
            Next <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>

        {!correct && payload?.record_id && sessionId && (
          <QuestionFlagTrigger
            recordId={payload.record_id}
            collection="fuel_questions"
            sessionId={sessionId}
          />
        )}
      </div>
    </div>
  );
}
